import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";
import AnimatedSection from "@/components/AnimatedSection";
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

type RiskLevel = "Low Risk" | "Moderate Risk" | "Elevated Risk" | "High Enforcement Focus";

const riskColors: Record<RiskLevel, string> = {
  "Low Risk": "bg-emerald-100 text-emerald-800",
  "Moderate Risk": "bg-amber-100 text-amber-800",
  "Elevated Risk": "bg-orange-100 text-orange-800",
  "High Enforcement Focus": "bg-red-100 text-red-800",
};

const riskBarWidth: Record<RiskLevel, string> = {
  "Low Risk": "w-1/4",
  "Moderate Risk": "w-2/4",
  "Elevated Risk": "w-3/4",
  "High Enforcement Focus": "w-full",
};

const riskBarColor: Record<RiskLevel, string> = {
  "Low Risk": "bg-emerald-500",
  "Moderate Risk": "bg-amber-500",
  "Elevated Risk": "bg-orange-500",
  "High Enforcement Focus": "bg-red-500",
};

const industries: { name: string; risk: RiskLevel }[] = [
  { name: "Technology & Digital Platforms", risk: "High Enforcement Focus" },
  { name: "Life Sciences & Pharmaceuticals", risk: "High Enforcement Focus" },
  { name: "Healthcare Markets", risk: "Elevated Risk" },
  { name: "Aerospace & Defense", risk: "Moderate Risk" },
  { name: "Transportation & Logistics", risk: "Moderate Risk" },
  { name: "Energy & Utilities", risk: "Elevated Risk" },
  { name: "Financial Services & Fintech", risk: "Moderate Risk" },
  { name: "Manufacturing & Industrial Markets", risk: "Low Risk" },
  { name: "Consumer Products & Retail", risk: "Moderate Risk" },
  { name: "Real Estate & Infrastructure", risk: "Low Risk" },
];

const recentEnforcement = [
  { deal: "Major Tech Platform Acquisition", status: "Challenged", agency: "DOJ", year: "2026" },
  { deal: "Pharmaceutical Horizontal Merger", status: "Consent Decree", agency: "FTC", year: "2026" },
  { deal: "Healthcare System Consolidation", status: "Under Review", agency: "FTC", year: "2025" },
  { deal: "Defense Contractor Vertical Merger", status: "Cleared with Conditions", agency: "DOJ", year: "2025" },
];

const MergerRiskRadar = () => {
  const [form, setForm] = useState({ industry: "", size: "", overlap: "", concentration: "", vertical: "" });
  const [result, setResult] = useState<string | null>(null);

  const assess = () => {
    let score = 0;
    const ind = industries.find((i) => i.name === form.industry);
    if (ind) {
      if (ind.risk === "High Enforcement Focus") score += 4;
      else if (ind.risk === "Elevated Risk") score += 3;
      else if (ind.risk === "Moderate Risk") score += 2;
      else score += 1;
    }
    if (form.size === "large") score += 3;
    else if (form.size === "medium") score += 2;
    else score += 1;
    if (form.overlap === "high") score += 3;
    else if (form.overlap === "moderate") score += 2;
    else score += 1;
    if (form.concentration === "high") score += 3;
    else if (form.concentration === "moderate") score += 2;
    else score += 1;
    if (form.vertical === "yes") score += 2;

    if (score >= 12) setResult("High regulatory scrutiny likely. Significant antitrust risk identified. Early engagement with antitrust counsel is strongly recommended.");
    else if (score >= 8) setResult("Elevated regulatory attention possible. The transaction may warrant pre-filing antitrust analysis and strategic planning.");
    else if (score >= 5) setResult("Moderate regulatory risk. Standard HSR review anticipated, but competitive overlap should be carefully evaluated.");
    else setResult("Lower regulatory risk profile. Standard merger review procedures should be sufficient, though counsel review is always advisable.");
  };

  return (
    <Layout>
      <PageHead title="Merger Risk Radar" description="Interactive antitrust merger risk dashboard by Alicia J. Batts tracking FTC and DOJ enforcement trends by industry. Assess merger scrutiny risk for deal planning." path="/merger-risk-radar" keywords="merger risk radar, Alicia Batts merger analysis, antitrust merger enforcement, FTC merger review, DOJ merger scrutiny, deal planning antitrust" />
      <section className="bg-hero text-primary-foreground py-20">
        <div className="editorial-container">
          <AnimatedSection animation="fade"><div className="gold-accent-line mb-6" /></AnimatedSection>
          <AnimatedSection animation="fade-up" delay={100}>
            <h1 className="text-4xl md:text-5xl font-serif font-semibold mb-4">Merger Risk Radar</h1>
          </AnimatedSection>
          <AnimatedSection animation="fade-up" delay={200}>
            <p className="text-lg opacity-80 max-w-2xl">Interactive dashboard tracking antitrust enforcement trends to help corporations and investors assess merger scrutiny risk.</p>
          </AnimatedSection>
        </div>
      </section>

      {/* Heat Map */}
      <section className="section-padding">
        <div className="editorial-container">
          <p className="text-xs font-semibold uppercase tracking-widest text-gold mb-6">Industry Risk Heat Map</p>
          <div className="space-y-3">
            {industries.map((ind) => (
              <div key={ind.name} className="bg-card rounded-md p-4 shadow-card border border-border flex flex-col sm:flex-row sm:items-center gap-3">
                <span className="text-sm font-medium text-foreground sm:w-64 shrink-0">{ind.name}</span>
                <div className="flex-1 bg-muted rounded-full h-3 overflow-hidden">
                  <div className={`h-full rounded-full ${riskBarColor[ind.risk]} ${riskBarWidth[ind.risk]} transition-all`} />
                </div>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${riskColors[ind.risk]}`}>{ind.risk}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Enforcement */}
      <section className="py-16 bg-muted/50">
        <div className="editorial-container">
          <p className="text-xs font-semibold uppercase tracking-widest text-gold mb-6">Recent Merger Enforcement</p>
          <div className="grid md:grid-cols-2 gap-4">
            {recentEnforcement.map((item, i) => (
              <div key={i} className="bg-card rounded-md p-6 shadow-card border border-border">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gold">{item.agency}</span>
                  <span className="text-xs text-muted-foreground">{item.year}</span>
                </div>
                <h3 className="font-serif text-base font-semibold text-foreground mb-1">{item.deal}</h3>
                <span className="text-sm text-muted-foreground">{item.status}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deal Planning Tool */}
      <section className="section-padding">
        <div className="editorial-container max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-gold mb-3">Deal Planning Tool</p>
          <h2 className="font-serif text-3xl font-semibold text-foreground mb-8">Assess Your Transaction Risk</h2>
          <div className="space-y-5">
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Industry</label>
              <select value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="w-full border border-border rounded-sm px-4 py-2.5 text-sm bg-card text-foreground">
                <option value="">Select industry</option>
                {industries.map((i) => <option key={i.name} value={i.name}>{i.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Transaction Size</label>
              <select value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} className="w-full border border-border rounded-sm px-4 py-2.5 text-sm bg-card text-foreground">
                <option value="">Select size</option>
                <option value="small">Under $500M</option>
                <option value="medium">$500M – $5B</option>
                <option value="large">Over $5B</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Market Overlap</label>
              <select value={form.overlap} onChange={(e) => setForm({ ...form, overlap: e.target.value })} className="w-full border border-border rounded-sm px-4 py-2.5 text-sm bg-card text-foreground">
                <option value="">Select overlap level</option>
                <option value="low">Low</option>
                <option value="moderate">Moderate</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Competitor Concentration</label>
              <select value={form.concentration} onChange={(e) => setForm({ ...form, concentration: e.target.value })} className="w-full border border-border rounded-sm px-4 py-2.5 text-sm bg-card text-foreground">
                <option value="">Select concentration</option>
                <option value="low">Low</option>
                <option value="moderate">Moderate</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-foreground block mb-2">Vertical Integration</label>
              <select value={form.vertical} onChange={(e) => setForm({ ...form, vertical: e.target.value })} className="w-full border border-border rounded-sm px-4 py-2.5 text-sm bg-card text-foreground">
                <option value="">Select</option>
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
            <button onClick={assess} className="w-full bg-primary text-primary-foreground py-3 rounded-sm font-medium text-sm hover:opacity-90 transition-opacity">
              Generate Risk Assessment
            </button>
            {result && (
              <div className="bg-card rounded-md p-6 shadow-elevated border border-border">
                <p className="text-sm font-semibold text-foreground mb-2">Preliminary Assessment</p>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">{result}</p>
                <Link to="/contact" className="inline-flex items-center gap-2 text-sm font-medium text-navy-500 hover:text-navy-700 transition-colors">
                  Contact Batts Legal LLP for Detailed Analysis <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default MergerRiskRadar;
